import { useEffect, useMemo, useState } from 'react'
import type { TmsInvoiceRow, TmsInvoiceWorklist } from '@numeo/types-main-web-app'
import type { QuickAnswerKind } from '@numeo/types'
import { ActiveFactoringProviderLabels } from '@numeo/types-core'
import { useFactoringStatusStore } from '@/hooks/factoringStatusStore'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { useFactoringBatchableStore } from '../../invoicing/factoringBatchableStore'
import { DeliveryStatsTable } from './DeliveryStatsTable'
import { factoringEligibilityOf, factoringOfferOf } from './factoringBatch'
import { FactoringBatchDrawer } from './FactoringBatchDrawer'
import { FactoringCard } from './FactoringCard'
import { InvoiceEmailDrawer } from './InvoiceEmailDrawer'
import { CreateBatchDrawer, type BatchMode } from './CreateBatchDrawer'
import { ReadyLoadsTable } from './ReadyLoadsTable'
import { SelectionBar } from './SelectionBar'
import { amountOf, asksAboutFactoring, isReadyListRequest, rowKeyOf, statusOf, withStoreRows, worklistRowsOf } from './invoicingRows'
import { EMPTY_KEYS, useInvoicingSelectionStore } from './invoicingStores'
import { useInvoiceReview } from './review/reviewStore'
import { useMoreWorklistRows } from './useMoreWorklistRows'

interface InvoicingTableProps {
    messageId: string
    worklist: TmsInvoiceWorklist
    /** The suggestion the answer came from: `submit` draws the delivery statistics above the table. */
    quickAnswer?: QuickAnswerKind
    /** The user's message the answer is for: a question for the ready list opens the table on Ready, any other on All loads (v17). */
    question?: string
}

/** A drawer open over the table: which one, and the rows it was opened with (a blocked one is removed from these, not from the table). */
interface OpenDrawer {
    kind: 'email' | 'factoring' | 'batch'
    keys: string[]
    /** The batch drawer's mode: a new batch, or an existing one. */
    batchMode?: BatchMode
}

/**
 * The invoicing answer in the chat (v17 flows 2 to 4, 13 to 15): the table with its filters and selection, the selection bar, the
 * email and factoring drawers, and the factoring offer. Rows read from the store, so a write updates its row in place.
 */
export const InvoicingTable = ({ messageId, worklist, quickAnswer, question }: InvoicingTableProps) => {
    const stored = useInvoicingRowsStore((state) => state.rows)
    // "Show more" pages every delivered load, so a windowed answer (`period`) only links to the Invoices page.
    const more = useMoreWorklistRows(worklist, !worklist.period)
    const rows = useMemo(() => {
        const first = worklistRowsOf(worklist)
        const keys = new Set(first.map(rowKeyOf))
        return withStoreRows([...first, ...more.rows.filter((row) => !keys.has(rowKeyOf(row)))], stored)
    }, [worklist, more.rows, stored])
    // A windowed read also counts the window's invoiced loads (`invoiced`), so the table's count is every load delivered in it.
    const total = worklist.ready.total + worklist.needs_documents.total + (worklist.invoiced?.total ?? 0)
    const workspace = (
        <InvoicingWorkspace
            messageId={messageId}
            rows={rows}
            total={total}
            preset={isReadyListRequest(question) ? 'ready' : 'all'}
            factoringAsked={quickAnswer === 'submit' || asksAboutFactoring(question)}
            more={more.hasMore ? { loading: more.loading, onMore: () => void more.loadMore() } : undefined}
        />
    )
    // v17 flow 13: the statistics, then the same table (on All loads) and the factoring offer.
    if (quickAnswer === 'submit') {
        return (
            <div className="flex flex-col gap-3">
                <DeliveryStatsTable rows={rows} period={worklist.period} />
                {workspace}
            </div>
        )
    }
    return workspace
}

interface InvoicingRowsTableProps {
    messageId: string
    /** The invoicing rows the answer listed, one per load. */
    rows: TmsInvoiceRow[]
    /** The whole count the answer stood for, when more than its rows. */
    total?: number
    question?: string
    /** Whether the factoring offer may follow the table (never under a single load's answer). */
    offerFactoring?: boolean
}

/**
 * Invoicing rows any other answer listed (the loads a user named, a Ready-tab list, one delivered load): the same v17 table, with its
 * filters, selection, Review drawer and send drawers, so a free-text answer reads the same as the suggestions' (decision 10).
 */
export const InvoicingRowsTable = ({ messageId, rows: listed, total, question, offerFactoring = true }: InvoicingRowsTableProps) => {
    const stored = useInvoicingRowsStore((state) => state.rows)
    const rows = useMemo(() => withStoreRows(listed, stored), [listed, stored])
    const preset = isReadyListRequest(question) ? 'ready' : 'all'
    return (
        <InvoicingWorkspace
            messageId={messageId}
            rows={rows}
            total={Math.max(total ?? 0, rows.length)}
            preset={preset}
            offerFactoring={offerFactoring}
            factoringAsked={asksAboutFactoring(question)}
        />
    )
}

interface InvoicingWorkspaceProps {
    messageId: string
    rows: TmsInvoiceRow[]
    total: number
    preset: 'all' | 'ready'
    /** Whether the factoring offer may follow the table at all. */
    offerFactoring?: boolean
    /** Whether the user asked about factoring; without it, only an invoice created and not sent in the table brings the offer. */
    factoringAsked: boolean
    /** The next rows, when the answer has more than it listed and they can be paged here. */
    more?: { loading: boolean; onMore: () => void }
}

const InvoicingWorkspace = ({ messageId, rows, total, preset, offerFactoring = true, factoringAsked, more }: InvoicingWorkspaceProps) => {
    const selectedKeys = useInvoicingSelectionStore((state) => state.byMessage[messageId] ?? EMPTY_KEYS)
    const clearSelection = useInvoicingSelectionStore((state) => state.clear)
    const setSelected = useInvoicingSelectionStore((state) => state.setSelected)
    const [drawer, setDrawer] = useState<OpenDrawer | null>(null)
    const [declined, setDeclined] = useState(false)

    const factoringProvider = useFactoringStatusStore((state) => state.activeProviders.main)
    const factoringLoaded = useFactoringStatusStore((state) => state.hasLoaded)
    const refreshFactoring = useFactoringStatusStore((state) => state.refresh)
    useEffect(() => {
        if (!factoringLoaded) void refreshFactoring()
    }, [factoringLoaded, refreshFactoring])
    const providerName = factoringProvider ? ActiveFactoringProviderLabels[factoringProvider] : ''
    // The offer counts every invoice of the company a batch takes, not only this table's rows (S17): read once per table drawn.
    const stored = useInvoicingRowsStore((state) => state.rows)
    const companyRows = useFactoringBatchableStore((state) => state.rows)
    const companyTotal = useFactoringBatchableStore((state) => state.total)
    const companyStatus = useFactoringBatchableStore((state) => state.status)
    const offersFactoring = offerFactoring && (factoringAsked || rows.some((row) => statusOf(row) === 'draft')) && Boolean(factoringProvider)
    useEffect(() => {
        if (!offersFactoring) return
        void useFactoringBatchableStore.getState().refresh()
    }, [offersFactoring])

    const selectedRows = rows.filter((row) => selectedKeys.includes(rowKeyOf(row)))
    const selectedTotal = selectedRows.reduce((sum, row) => sum + amountOf(row), 0)
    const { eligible } = factoringEligibilityOf(rows)
    const company = useMemo(() => ({ rows: withStoreRows(companyRows, stored), total: companyTotal }), [companyRows, companyTotal, stored])
    const offer = useMemo(() => factoringOfferOf(eligible, company), [eligible, company])

    const openDrawer = (kind: OpenDrawer['kind'], keys: string[], batchMode?: BatchMode) => setDrawer({ kind, keys, batchMode })
    const dropFromDrawer = (key: string) => {
        setDrawer((current) => (current ? { ...current, keys: current.keys.filter((k) => k !== key) } : current))
        setSelected(messageId, key, false)
    }
    const finishDrawer = () => {
        setDrawer(null)
        clearSelection(messageId)
    }
    // The offer's drawer may list the company's other batchable invoices too, which this table does not show.
    const drawerRows = (keys: string[]) => [...rows, ...offer.rows.filter((row) => !rows.includes(row))].filter((row) => keys.includes(rowKeyOf(row)))

    return (
        <div className="flex flex-col gap-3">
            <ReadyLoadsTable messageId={messageId} rows={rows} preset={preset} total={total} more={more} />

            {selectedRows.length > 0 && (
                <SelectionBar
                    rows={selectedRows}
                    total={selectedTotal}
                    factoringConnected={Boolean(factoringProvider)}
                    onCreateInvoice={(row) => useInvoiceReview.getState().open(rowKeyOf(row), 'review', row.load_id)}
                    onBatch={(mode) => openDrawer('batch', selectedRows.map(rowKeyOf), mode)}
                    onSend={() => openDrawer(factoringProvider ? 'factoring' : 'email', selectedRows.map(rowKeyOf))}
                />
            )}

            {offersFactoring && !declined && (
                <FactoringCard
                    provider={providerName}
                    eligibleCount={offer.count}
                    fromTable={eligible.length}
                    checking={companyStatus === 'idle' || companyStatus === 'loading'}
                    unchecked={companyStatus === 'failed'}
                    onRetry={() => void useFactoringBatchableStore.getState().refresh()}
                    onReview={() => openDrawer('factoring', offer.rows.map(rowKeyOf))}
                    onDismiss={() => setDeclined(true)}
                />
            )}

            <InvoiceEmailDrawer
                open={drawer?.kind === 'email'}
                rows={drawer?.kind === 'email' ? drawerRows(drawer.keys) : []}
                onClose={() => setDrawer(null)}
                onRemove={dropFromDrawer}
                onDone={finishDrawer}
            />
            <CreateBatchDrawer
                open={drawer?.kind === 'batch'}
                mode={drawer?.batchMode ?? 'new'}
                rows={drawer?.kind === 'batch' ? drawerRows(drawer.keys) : []}
                onClose={() => setDrawer(null)}
                onRemove={dropFromDrawer}
                onDone={finishDrawer}
            />
            <FactoringBatchDrawer
                open={drawer?.kind === 'factoring'}
                provider={providerName}
                rows={drawer?.kind === 'factoring' ? drawerRows(drawer.keys) : []}
                onClose={() => setDrawer(null)}
                onRemove={dropFromDrawer}
                onDone={finishDrawer}
            />
        </div>
    )
}
